import { useEffect } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { getContrastTextColor } from '@vibesites/tokens';
import {
  useContentToken,
  useSlotGroup,
  useCollectionBinding,
  usePropertyAddressUrl,
  usePropertyName,
  usePrimaryColor,
  useSecondaryColor,
} from '@vibesites/runtime/react';
import {
  EditableToken,
  EditableSlotGroup,
  EditableCollectionBinding,
  RichTextContent,
} from '@vibesites/runtime/react';
import {
  MultihubLogoMonoWhite,
  MultihubLogoMonoBlack,
  repliWhiteLogoUrl,
  FairHousingIcon,
  PetsAllowedIcon,
  WheelchairIcon,
} from '@vibesites/assets';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFacebook, faGoogle, faInstagram, faXTwitter } from '@fortawesome/free-brands-svg-icons';
// Local black variant of the Repli wordmark — `@vibesites/assets` only ships
// `repliWhiteLogoUrl`, so we keep a dark-fill copy for light primary backgrounds.
import repliBlackLogoUrl from '../assets/repli_black.svg';

// ============================================================================
// Contrast helpers
// ============================================================================

const TEXT_ON_PRIMARY_LIGHT = '#ffffff';
/** WCAG AA contrast ratio for normal-size text (legal links are ~10px). */
const MIN_ACCENT_CONTRAST_RATIO = 4.5;

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  let cleaned = hex.replace('#', '').trim();
  if (cleaned.length === 4) cleaned = cleaned.slice(0, 3);
  if (cleaned.length === 8) cleaned = cleaned.slice(0, 6);
  if (cleaned.length === 3) {
    return {
      r: parseInt(cleaned[0] + cleaned[0], 16),
      g: parseInt(cleaned[1] + cleaned[1], 16),
      b: parseInt(cleaned[2] + cleaned[2], 16),
    };
  }
  if (cleaned.length === 6) {
    return {
      r: parseInt(cleaned.slice(0, 2), 16),
      g: parseInt(cleaned.slice(2, 4), 16),
      b: parseInt(cleaned.slice(4, 6), 16),
    };
  }
  return null;
}

function getRelativeLuminance(hex: string): number {
  const rgb = hexToRgb(hex);
  if (!rgb) return 0;
  const [rs, gs, bs] = [rgb.r / 255, rgb.g / 255, rgb.b / 255].map((c) =>
    c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  );
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

/** WCAG contrast ratio between two hex colors. Returns 0 if either can't be parsed. */
function getContrastRatio(foreground: string, background: string): number {
  const fgLum = getRelativeLuminance(foreground);
  const bgLum = getRelativeLuminance(background);
  if (!hexToRgb(foreground) || !hexToRgb(background)) return 0;
  const lighter = Math.max(fgLum, bgLum);
  const darker = Math.min(fgLum, bgLum);
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Prefer secondary as the accent on a primary background when it meets WCAG AA.
 * Otherwise fall back to white-on-dark / black-on-light via `getContrastTextColor`.
 */
function resolveAccentOnPrimary(
  secondary: string | null | undefined,
  primary: string | null | undefined
): string {
  const bg = primary || '#000000';
  if (secondary && getContrastRatio(secondary, bg) >= MIN_ACCENT_CONTRAST_RATIO) {
    return secondary;
  }
  return getContrastTextColor(bg);
}

// ============================================================================
// Types
// ============================================================================

interface OfficeHour {
  day?: string | null;
  open_time?: string | null;
  close_time?: string | null;
}

function hasValue(val: string | null | undefined): val is string {
  if (!val) return false;
  const normalized = val.trim().toLowerCase();
  return normalized !== '' && normalized !== 'false' && normalized !== 'null' && normalized !== 'undefined';
}

/** Formats a phone string into XXX-XXX-XXXX to match the Roxanne demo footer. */
function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
}

/**
 * Split a full address so street (and suite) stay on line 1 and
 * "City, ST ZIP" move to line 2. Falls back to the first comma when the
 * trailing city/state/zip pattern is missing.
 */
function formatAddressLines(raw: string): { street: string; cityStateZip: string } | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;

  const cityStateZipMatch = trimmed.match(
    /^(.*?),\s*([^,]+,\s*[A-Za-z]{2}\s+\d{5}(?:-\d{4})?)\s*$/
  );
  if (cityStateZipMatch) {
    return {
      street: cityStateZipMatch[1].trim(),
      cityStateZip: cityStateZipMatch[2].trim(),
    };
  }

  const comma = trimmed.indexOf(',');
  if (comma === -1) return null;
  return {
    street: trimmed.slice(0, comma).trim(),
    cityStateZip: trimmed.slice(comma + 1).trim(),
  };
}

function formatTime(raw: string): string {
  const trimmed = raw.trim();
  if (/[ap]m/i.test(trimmed)) {
    return trimmed.replace(/([ap]m)/i, (m) => m.toLowerCase());
  }
  const match = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return trimmed;

  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const period = hours >= 12 ? 'pm' : 'am';
  if (hours === 0) hours = 12;
  else if (hours > 12) hours -= 12;
  return `${hours}:${minutes} ${period}`;
}

const DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_SHORT_LABELS: Record<string, string> = {
  mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu',
  fri: 'Fri', sat: 'Sat', sun: 'Sun',
};

interface GroupedHours {
  shortLabel: string;
  hours: string;
}

function normalizeDay(day: string): string {
  return day.trim().toLowerCase().slice(0, 3);
}

function groupOfficeHours(raw: OfficeHour[]): GroupedHours[] {
  const sorted = [...raw].sort((a, b) => {
    const ai = DAY_ORDER.indexOf(normalizeDay(a.day || ''));
    const bi = DAY_ORDER.indexOf(normalizeDay(b.day || ''));
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });

  const groups: GroupedHours[] = [];

  for (const entry of sorted) {
    if (!hasValue(entry.day)) continue;

    const key = normalizeDay(entry.day);
    const dayShortLabel = DAY_SHORT_LABELS[key] || (entry.day || '').slice(0, 3);
    const isClosed = !hasValue(entry.open_time) && !hasValue(entry.close_time);
    const hoursStr = isClosed
      ? 'Closed'
      : `${formatTime(entry.open_time || '')} - ${formatTime(entry.close_time || '')}`;

    const prev = groups[groups.length - 1];
    if (prev && prev.hours === hoursStr) {
      const shortDash = prev.shortLabel.indexOf(' - ');
      const startShort = shortDash !== -1 ? prev.shortLabel.slice(0, shortDash) : prev.shortLabel;
      prev.shortLabel = `${startShort} - ${dayShortLabel}`;
    } else {
      groups.push({ shortLabel: dayShortLabel, hours: hoursStr });
    }
  }

  return groups;
}

// ============================================================================
// Slot Group Definitions
// ============================================================================

const logoSlotDef: SlotGroupDefinition = {
  label: 'Footer Logo',
  fields: {
    image: {
      label: 'Logo Image',
      type: 'image',
      defaultField: 'property_mono_white_logo',
      suggestedFields: ['property_mono_white_logo', 'property_primary_logo', 'property_secondary_logo'],
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
  },
};

const managedByLogoDef: SlotGroupDefinition = {
  label: 'Managed By Logo',
  fields: {
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Multihub' },
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.multihub.io/' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const designByLogoDef: SlotGroupDefinition = {
  label: 'Website Design By Logo',
  fields: {
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Repli Logo' },
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.repli360.com/' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const addressLinkDef: SlotGroupDefinition = {
  label: 'Address',
  fields: {
    text: {
      label: 'Address Text',
      type: 'text',
      defaultField: 'property_full_address',
      defaultValue: '123 Main Street, City, ST 00000',
    },
    url: { label: 'URL', type: 'url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const accessibilityLinkDef: SlotGroupDefinition = {
  label: 'Accessibility Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Accessibility' },
    url: { label: 'URL', type: 'url', defaultValue: '/accessibility' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const privacyPolicyLinkDef: SlotGroupDefinition = {
  label: 'Privacy Policy Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Privacy Policy' },
    url: { label: 'URL', type: 'url', defaultValue: '/privacy-policy' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const sitemapLinkDef: SlotGroupDefinition = {
  label: 'Sitemap Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Sitemap' },
    url: { label: 'URL', type: 'url', defaultValue: '/sitemap' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const facebookLinkDef: SlotGroupDefinition = {
  label: 'Facebook',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_facebook_url',
      suggestedFields: ['property_facebook_url'],
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const twitterLinkDef: SlotGroupDefinition = {
  label: 'Twitter / X',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_twitter_url',
      suggestedFields: ['property_twitter_url'],
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const instagramLinkDef: SlotGroupDefinition = {
  label: 'Instagram',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_instagram_url',
      suggestedFields: ['property_instagram_url'],
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const googleLinkDef: SlotGroupDefinition = {
  label: 'Google Business',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_google_business_url',
      suggestedFields: ['property_google_business_url'],
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const socialIconClasses =
  'inline-flex items-center justify-center w-[30px] h-[30px] rounded-full bg-white text-[var(--vibe-color-secondary)] hover:opacity-80 transition-opacity';

// ============================================================================
// Component
// ============================================================================

export function Footer() {
  const phone = useContentToken('footer.phone');
  const propertyName = usePropertyName();
  const primaryColor = usePrimaryColor();
  const secondaryColor = useSecondaryColor();

  // Body / icons / credits use the auto-paired text-on-primary token.
  // Accent labels (Contact, Office Hours, legal links) prefer secondary when
  // it clears WCAG AA against primary; otherwise white or black.
  const accentColor = resolveAccentOnPrimary(secondaryColor, primaryColor);
  const textOnPrimary = getContrastTextColor(primaryColor || '#000000');
  const useWhiteOnPrimary = textOnPrimary === TEXT_ON_PRIMARY_LIGHT;

  const runtimeLogoSlotDef: SlotGroupDefinition = useWhiteOnPrimary
    ? logoSlotDef
    : {
        ...logoSlotDef,
        fields: {
          ...logoSlotDef.fields,
          image: {
            ...logoSlotDef.fields.image,
            defaultField: 'property_mono_black_logo',
          },
        },
      };

  const logo = useSlotGroup('footer.logo', runtimeLogoSlotDef);
  const managedByLogo = useSlotGroup('footer.managed_by_logo', managedByLogoDef);
  const designByLogo = useSlotGroup('footer.design_by_logo', designByLogoDef);
  const addressLink = useSlotGroup('footer.address', addressLinkDef);
  const propertyAddressUrl = usePropertyAddressUrl();
  const accessibilityLink = useSlotGroup('footer.accessibility_link', accessibilityLinkDef);
  const privacyPolicyLink = useSlotGroup('footer.privacy_policy_link', privacyPolicyLinkDef);
  const sitemapLink = useSlotGroup('footer.sitemap_link', sitemapLinkDef);
  const facebook = useSlotGroup('footer.facebook_link', facebookLinkDef);
  const twitter = useSlotGroup('footer.twitter_link', twitterLinkDef);
  const instagram = useSlotGroup('footer.instagram_link', instagramLinkDef);
  const google = useSlotGroup('footer.google_link', googleLinkDef);

  const officeHours = useCollectionBinding<OfficeHour>('footer.office_hours', {
    collection: 'office_hours',
  });

  const groupedHours = officeHours && officeHours.length > 0
    ? groupOfficeHours(officeHours)
    : [];

  const hasSocial = !!(facebook.url || twitter.url || instagram.url || google.url);

  const addressText = addressLink.text || '123 Main Street, City, ST 00000';
  const addressLines = formatAddressLines(addressText);

  // ─── RentCafe IQ chatbot z-index pin ────────────────────────────────────────
  // The Yardi RentCafe IQ widget is injected at runtime by an external script.
  // Scoped to the Footer because Footer is rendered on every page via
  // DefaultLayout, so we get a single global mount point.
  useEffect(() => {
    if (typeof document === 'undefined') return;

    const PATTERNS = [
      /\brcc-app/i,
      /\bwebchat-toggle/i,
      /conversations-webchat/i,
      /rentcafe/i,
      /rciq/i,
      /\bcrm[-_ ]?iq\b/i,
      /\biq[-_ ]?(chat|bot)/i,
      /yardi/i,
      /yvenue/i,
    ];

    const Z = '2147483647';

    function attrHaystack(el: Element): string {
      const id = el.id || '';
      const cls = typeof el.className === 'string'
        ? el.className
        : ((el.className as unknown as { baseVal?: string })?.baseVal || '');
      const src = el.getAttribute('src') || '';
      const title = el.getAttribute('title') || '';
      const name = el.getAttribute('name') || '';
      let data = '';
      for (const a of Array.from(el.attributes)) {
        if (a.name.startsWith('data-')) data += ` ${a.name} ${a.value}`;
      }
      return `${id} ${cls} ${src} ${title} ${name}${data}`;
    }

    function looksLikeRciq(el: Element): boolean {
      const hay = attrHaystack(el);
      return PATTERNS.some((p) => p.test(hay));
    }

    function outermostFixedAncestor(el: Element): HTMLElement | null {
      let cur: Element | null = el;
      let last: HTMLElement | null = null;
      while (cur && cur !== document.body && cur instanceof HTMLElement) {
        const pos = getComputedStyle(cur).position;
        if (pos === 'fixed') last = cur;
        cur = cur.parentElement;
      }
      return last;
    }

    function pin(el: HTMLElement) {
      el.style.setProperty('z-index', Z, 'important');
    }

    function processCandidate(el: Element) {
      if (!(el instanceof HTMLElement)) return;
      if (!looksLikeRciq(el)) return;
      pin(el);
      const fixedAncestor = outermostFixedAncestor(el);
      if (fixedAncestor && fixedAncestor !== el) pin(fixedAncestor);
    }

    function scanSubtree(root: ParentNode) {
      const candidates = root.querySelectorAll('iframe, div, aside, section, button');
      for (const c of Array.from(candidates)) processCandidate(c);
      if (root instanceof Element) processCandidate(root);
    }

    scanSubtree(document.body);

    let scheduled = false;
    const observer = new MutationObserver((records) => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        for (const r of records) {
          for (const node of Array.from(r.addedNodes)) {
            if (node instanceof Element) scanSubtree(node);
          }
          if (r.type === 'attributes' && r.target instanceof Element) {
            processCandidate(r.target);
          }
        }
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['id', 'class', 'src', 'title', 'name', 'style'],
    });

    return () => observer.disconnect();
  }, []);

  return (
    <footer
      className="text-primary-foreground"
      style={{ backgroundColor: 'var(--vibe-color-primary)' }}
    >
      {/* ---- Main Footer: 4 columns ---- */}
      <div className="max-w-[1290px] mx-auto px-6 sm:px-10 lg:px-12 pt-12 pb-10 md:pt-16 md:pb-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">

          {/* Column 1: Logo + Socials */}
          <div className="flex flex-col items-center lg:items-start gap-4">
            <EditableSlotGroup groupPath="footer.logo" definition={runtimeLogoSlotDef}>
              <a href="/" className="block">
                {logo.image ? (
                  <VibesitesImage
                    url={logo.image}
                    alt={logo.alt || 'Community Logo'}
                    className="h-auto w-auto max-h-16 max-w-[200px] object-contain"
                    sizes="200px"
                  />
                ) : (
                  <div
                    className="font-heading text-3xl"
                    style={{ color: accentColor }}
                  >
                    {propertyName || 'Our Community'}
                  </div>
                )}
              </a>
            </EditableSlotGroup>

            {hasSocial ? (
              <div className="flex items-center justify-center lg:justify-start gap-2.5">
                {facebook.url && (
                  <EditableSlotGroup groupPath="footer.facebook_link" definition={facebookLinkDef}>
                    <a
                      href={facebook.url}
                      target={facebook._target || '_blank'}
                      rel="noopener noreferrer"
                      aria-label="Facebook"
                      className={socialIconClasses}
                    >
                      <FontAwesomeIcon icon={faFacebook} className="w-4 h-4" />
                    </a>
                  </EditableSlotGroup>
                )}
                {twitter.url && (
                  <EditableSlotGroup groupPath="footer.twitter_link" definition={twitterLinkDef}>
                    <a
                      href={twitter.url}
                      target={twitter._target || '_blank'}
                      rel="noopener noreferrer"
                      aria-label="Twitter"
                      className={socialIconClasses}
                    >
                      <FontAwesomeIcon icon={faXTwitter} className="w-3.5 h-3.5" />
                    </a>
                  </EditableSlotGroup>
                )}
                {instagram.url && (
                  <EditableSlotGroup groupPath="footer.instagram_link" definition={instagramLinkDef}>
                    <a
                      href={instagram.url}
                      target={instagram._target || '_blank'}
                      rel="noopener noreferrer"
                      aria-label="Instagram"
                      className={socialIconClasses}
                    >
                      <FontAwesomeIcon icon={faInstagram} className="w-4 h-4" />
                    </a>
                  </EditableSlotGroup>
                )}
                {google.url && (
                  <EditableSlotGroup groupPath="footer.google_link" definition={googleLinkDef}>
                    <a
                      href={google.url}
                      target={google._target || '_blank'}
                      rel="noopener noreferrer"
                      aria-label="Google Business"
                      className={socialIconClasses}
                    >
                      <FontAwesomeIcon icon={faGoogle} className="w-4 h-4" />
                    </a>
                  </EditableSlotGroup>
                )}
              </div>
            ) : null}
          </div>

          {/* Column 2: Contact */}
          <div className="flex flex-col items-center sm:items-start text-center sm:text-left gap-3">
            <RichTextContent
              path="footer.contact_label"
              defaultValue="<p>CONTACT</p>"
              as="p"
              className="font-body text-xl leading-none"
              style={{ color: accentColor }}
            />
            <a
              href={`tel:${(phone || '').replace(/[^+\d]/g, '')}`}
              className="dniphone dniphonehref font-body text-[13px] text-primary-foreground hover:opacity-70 transition-opacity"
            >
              <EditableToken
                path="footer.phone"
                defaultField="property_call_tracking_phone"
                defaultValue="123-456-7890"
              >
                {formatPhone(phone)}
              </EditableToken>
            </a>
            <EditableSlotGroup groupPath="footer.address" definition={addressLinkDef}>
              <a
                href={addressLink.url || propertyAddressUrl || '#'}
                target={addressLink._target || '_blank'}
                rel="noopener noreferrer"
                className="font-body text-[13px] text-primary-foreground hover:opacity-70 transition-opacity block max-w-[220px]"
              >
                {addressLines ? (
                  <>
                    <span className="block">{addressLines.street}</span>
                    <span className="block">{addressLines.cityStateZip}</span>
                  </>
                ) : (
                  addressText
                )}
              </a>
            </EditableSlotGroup>
          </div>

          {/* Column 3: Office Hours */}
          <div className="flex flex-col items-center sm:items-start text-center sm:text-left">
            <RichTextContent
              path="footer.office_hours_label"
              defaultValue="<p>OFFICE HOURS</p>"
              as="p"
              className="font-body text-xl leading-none mb-3"
              style={{ color: accentColor }}
            />
            <EditableCollectionBinding bindingPath="footer.office_hours" label="Office Hours">
              {groupedHours.length > 0 ? (
                <dl className="space-y-2 w-full max-w-[260px]">
                  {groupedHours.map((group, index) => (
                    <div
                      key={index}
                      className="flex justify-between gap-4 font-body text-[13px] text-primary-foreground"
                    >
                      <dt>{group.shortLabel}</dt>
                      <dd className="text-right">{group.hours}</dd>
                    </div>
                  ))}
                </dl>
              ) : (
                <p className="font-body text-[13px] text-primary-foreground/40">
                  Office hours will be displayed here.
                </p>
              )}
            </EditableCollectionBinding>
          </div>

          {/* Column 4: Compliance icons + legal links (icon centered above each link) */}
          <nav
            className="flex items-end justify-center lg:justify-start font-body text-[10px] leading-none"
            style={{ color: accentColor }}
            aria-label="Legal links"
          >
            <div className="flex flex-col items-center gap-3 text-primary-foreground">
              <FairHousingIcon size={28} color="currentColor" className="block" />
              <EditableSlotGroup
                groupPath="footer.accessibility_link"
                definition={accessibilityLinkDef}
                className="leading-none"
              >
                <a
                  href={accessibilityLink.url || '/accessibility'}
                  target={accessibilityLink._target || '_self'}
                  className="block leading-none hover:opacity-70 transition-opacity"
                  style={{ color: accentColor }}
                >
                  {accessibilityLink.text || 'Accessibility'}
                </a>
              </EditableSlotGroup>
            </div>
            <span className="px-1.5 leading-none self-end" style={{ color: accentColor }} aria-hidden="true">
              |
            </span>
            <div className="flex flex-col items-center gap-3 text-primary-foreground">
              <PetsAllowedIcon size={28} color="currentColor" className="block leading-none" />
              <EditableSlotGroup
                groupPath="footer.sitemap_link"
                definition={sitemapLinkDef}
                className="leading-none"
              >
                <a
                  href={sitemapLink.url || '/sitemap'}
                  target={sitemapLink._target || '_self'}
                  className="block leading-none hover:opacity-70 transition-opacity"
                  style={{ color: accentColor }}
                >
                  {sitemapLink.text || 'Sitemap'}
                </a>
              </EditableSlotGroup>
            </div>
            <span className="px-1.5 leading-none self-end" style={{ color: accentColor }} aria-hidden="true">
              |
            </span>
            <div className="flex flex-col items-center gap-3 text-primary-foreground">
              <WheelchairIcon size={28} color="currentColor" className="block leading-none" />
              <EditableSlotGroup
                groupPath="footer.privacy_policy_link"
                definition={privacyPolicyLinkDef}
                className="leading-none"
              >
                <a
                  href={privacyPolicyLink.url || '/privacy-policy'}
                  target={privacyPolicyLink._target || '_self'}
                  className="block leading-none hover:opacity-70 transition-opacity"
                  style={{ color: accentColor }}
                >
                  {privacyPolicyLink.text || 'Privacy Policy'}
                </a>
              </EditableSlotGroup>
            </div>
          </nav>
        </div>
      </div>

      {/* ---- Divider ---- */}
      {/* Slash opacity on hex CSS vars is unreliable, so pick the border from
          the same white/dark branch used for logos. */}
      <div className="max-w-[1290px] mx-auto px-6 sm:px-10 lg:px-12">
        <hr className={`border-t ${useWhiteOnPrimary ? 'border-white/80' : 'border-[#1a1a1a]/80'}`} />
      </div>

      {/* ---- Bottom bar: Managed By | Copyright | Design By ---- */}
      <div className="max-w-[1290px] mx-auto px-6 sm:px-10 lg:px-12 py-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 font-body text-xs text-primary-foreground">
          {/* Managed By (Multihub) */}
          <EditableSlotGroup
            groupPath="footer.managed_by_logo"
            definition={managedByLogoDef}
            className="flex justify-center md:justify-start"
          >
            <a
              href={managedByLogo.url || 'https://www.multihub.io/'}
              target={managedByLogo._target || '_blank'}
              rel="noopener noreferrer"
              className="flex flex-col items-center md:items-start gap-1 opacity-90 hover:opacity-100 transition-opacity"
            >
              <RichTextContent
                path="footer.managed_by_text"
                defaultValue="<p>Managed By</p>"
                as="span"
                className="text-[11px] text-primary-foreground"
              />
              {useWhiteOnPrimary ? (
                <MultihubLogoMonoWhite height={18} alt={managedByLogo.alt || 'Multihub'} />
              ) : (
                <MultihubLogoMonoBlack height={18} alt={managedByLogo.alt || 'Multihub'} />
              )}
            </a>
          </EditableSlotGroup>

          {/* Copyright */}
          <p className="text-center text-[12px] text-primary-foreground">
            © {new Date().getFullYear()} All Rights Reserved
            {propertyName ? ` | ${propertyName}` : ''}
          </p>

          {/* Website Design By (Repli) */}
          <EditableSlotGroup
            groupPath="footer.design_by_logo"
            definition={designByLogoDef}
            className="flex justify-center md:justify-end"
          >
            <a
              href={designByLogo.url || 'https://www.repli360.com/'}
              target={designByLogo._target || '_blank'}
              rel="noopener noreferrer"
              className="flex flex-col items-center md:items-end gap-1 opacity-90 hover:opacity-100 transition-opacity"
            >
              <RichTextContent
                path="footer.design_by_text"
                defaultValue="<p>Website Design By</p>"
                as="span"
                className="text-[11px] text-primary-foreground"
              />
              <VibesitesImage
                url={useWhiteOnPrimary ? repliWhiteLogoUrl : repliBlackLogoUrl}
                alt={designByLogo.alt || 'Repli Logo'}
                height={16}
                className="h-4 w-auto"
                sizes="80px"
                priority
              />
            </a>
          </EditableSlotGroup>
        </div>
      </div>

      <style>{`
        .rcc-app-cta-wrap,
        .rcc-app-controls,
        [class*="rcc-app-"],
        [id*="conversations-webchat"],
        [class*="webchat-toggle"],
        iframe[src*="rentcafeiq"],
        iframe[src*="rentcafe-iq"],
        iframe[src*="rentcafe.com/iq"],
        iframe[src*="yardi"],
        iframe[src*="yvenue"],
        iframe[title*="RentCafe IQ" i],
        iframe[title*="Rent Cafe IQ" i],
        iframe[title*="CRM IQ" i],
        iframe[title*="webchat" i],
        [id^="rciq"],
        [id*="rentcafeiq"],
        [id*="rentcafe-iq"],
        [id*="rentcafe_iq"],
        [id*="iq-chat"],
        [id*="iqchat"],
        [id*="iq-bot"],
        [id*="iqbot"],
        [class^="rciq-"],
        [class*=" rciq-"],
        [class*="rentcafeiq"],
        [class*="rentcafe-iq"],
        [class*="rentcafe_iq"],
        [class*="iq-chat"],
        [class*="iqchat"],
        [class*="yardi-chat"],
        [class*="yvenue"],
        [data-rciq],
        [data-rentcafeiq] {
          z-index: 2147483647 !important;
        }
      `}</style>
    </footer>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Footer',
  displayName: 'Footer',
  tokens: {
    'footer.phone': {
      label: 'Phone',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      defaultValue: '123-456-7890',
    },
    'footer.contact_label': {
      label: 'Contact Label',
      type: 'richtext',
      defaultValue: '<p>CONTACT</p>',
    },
    'footer.office_hours_label': {
      label: 'Office Hours Label',
      type: 'richtext',
      defaultValue: '<p>OFFICE HOURS</p>',
    },
    'footer.managed_by_text': {
      label: 'Managed By Text',
      type: 'richtext',
      defaultValue: '<p>Managed By</p>',
    },
    'footer.design_by_text': {
      label: 'Website Design By Text',
      type: 'richtext',
      defaultValue: '<p>Website Design By</p>',
    },
  },
  slotGroups: {
    'footer.logo': logoSlotDef,
    'footer.managed_by_logo': managedByLogoDef,
    'footer.design_by_logo': designByLogoDef,
    'footer.address': addressLinkDef,
    'footer.accessibility_link': accessibilityLinkDef,
    'footer.privacy_policy_link': privacyPolicyLinkDef,
    'footer.sitemap_link': sitemapLinkDef,
    'footer.facebook_link': facebookLinkDef,
    'footer.twitter_link': twitterLinkDef,
    'footer.instagram_link': instagramLinkDef,
    'footer.google_link': googleLinkDef,
  },
  collectionBindings: {
    'footer.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(Footer as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default Footer;
